// ===========================================
// Week Items Preview
// ===========================================
// The line items the manual-invoice week selector will create — the SERVER's
// pricing and wording (POST …/billable-context/preview), exactly as the
// family will read them — so a start date ("start date dd/mm/yyyy") or a merged
// summary is seen BEFORE the invoice exists (no weeks are persisted per line, so
// a wrong description cannot be fixed from the data afterwards). A description
// over the backend's 200-character limit is flagged here; the parent dialog
// blocks submit on the same `overlongItems` check. Shared by the Create + Edit
// dialogs.

import { Alert, Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { CreateInvoiceItemInput } from '../../api/invoices.api';
import { MAX_LINE_ITEM_DESCRIPTION_LENGTH } from './lineItemWeekBuilder';

interface WeekItemsPreviewProps {
  items: CreateInvoiceItemInput[];
  /** Optional caption above the list, e.g. the grouping mode. */
  caption?: string;
  /** The server is re-pricing a changed selection; the list shown is the previous one. */
  updating?: boolean;
}

const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

export default function WeekItemsPreview({ items, caption, updating = false }: WeekItemsPreviewProps) {
  if (items.length === 0) return null;
  const hasOverlong = items.some((i) => i.description.length > MAX_LINE_ITEM_DESCRIPTION_LENGTH);

  return (
    <Box sx={{ mt: 2 }} data-testid="week-items-preview">
      <Typography variant="caption" color="text.secondary">
        {items.length} line item{items.length === 1 ? '' : 's'}
        {caption ? ` · ${caption}` : ''}
        {updating ? ' · updating…' : ''}
      </Typography>
      <List dense disablePadding sx={{ opacity: updating ? 0.5 : 1 }} aria-busy={updating || undefined}>
        {items.map((item, index) => {
          const tooLong = item.description.length > MAX_LINE_ITEM_DESCRIPTION_LENGTH;
          return (
            <ListItem key={`${index}-${item.description}`} disableGutters>
              <ListItemText
                primary={item.description}
                secondary={
                  tooLong
                    ? `${item.description.length}/${MAX_LINE_ITEM_DESCRIPTION_LENGTH} characters — too long`
                    : `${item.quantity} × ${currency.format(item.unitPrice)}`
                }
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ color: tooLong ? 'error' : 'text.secondary' }}
              />
            </ListItem>
          );
        })}
      </List>
      {hasOverlong && (
        <Alert severity="error" sx={{ mt: 1 }}>
          A line item description is longer than {MAX_LINE_ITEM_DESCRIPTION_LENGTH} characters, so
          the invoice would be rejected. Bill some of these lessons on a separate invoice to
          shorten it.
        </Alert>
      )}
    </Box>
  );
}
